/**
 * SafeError — persistent, contextual, safe error surface (Design System §5)
 *
 * - Persistent: stays until the underlying problem is resolved; never
 *   auto-expires. Finance errors must not vanish before they are read.
 * - Safe: shows a human explanation written by us, never raw server or
 *   transport text.
 * - Traceable: carries the server reference ID so support can correlate.
 * - Recoverable: offers an explicit retry verb and a slot for further safe
 *   destination actions.
 */

import React from 'react';
import { t } from '@budgeting101/i18n';
import { Button } from './Button.js';

export interface SafeErrorProps {
  /** What failed, in the user's terms, e.g. "Couldn't load cash position". */
  title: string;
  /** Safe human explanation of what this means for the numbers on screen. */
  message: string;
  /** Server-issued reference ID for support correlation. */
  referenceId?: string;
  /** Explicit retry action. Rendered only when provided. */
  onRetry?: () => void;
  /** Exact verb for the retry action. Defaults to "Try again". */
  retryLabel?: string;
  /** Whether the retry request is currently in flight. */
  retrying?: boolean;
  /** Additional safe destination actions (links, secondary buttons). */
  children?: React.ReactNode;
  className?: string;
}

export function SafeError({
  title,
  message,
  referenceId,
  onRetry,
  retryLabel,
  retrying = false,
  children,
  className,
}: SafeErrorProps) {
  const resolvedRetryLabel = retryLabel ?? t('ui.tryAgain');

  return (
    <div
      className={['ui-safe-error', className ?? ''].filter(Boolean).join(' ')}
      role="alert"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
        padding: 'var(--space-4)',
        backgroundColor: 'var(--color-surface-subtle)',
        border: 'var(--border-width-default) solid var(--color-danger)',
        borderRadius: 'var(--radius-md)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)' }}>
        <span aria-hidden="true" style={{ color: 'var(--color-danger)' }}>⚠</span>
        <span
          className="ui-safe-error__title"
          style={{
            fontSize: 'var(--text-body-strong-size)',
            fontWeight: 'var(--text-body-strong-weight)',
            color: 'var(--color-text)',
          }}
        >
          {title}
        </span>
      </div>

      <p
        className="ui-safe-error__message"
        style={{
          margin: 0,
          fontSize: 'var(--text-body-size)',
          lineHeight: 'var(--text-body-lh)',
          color: 'var(--color-text-secondary)',
        }}
      >
        {message}
      </p>

      {(onRetry || children) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--space-3)' }}>
          {onRetry && (
            <Button variant="secondary" size="sm" onClick={onRetry} loading={retrying}>
              {resolvedRetryLabel}
            </Button>
          )}
          {children}
        </div>
      )}

      {referenceId && (
        <span
          className="ui-safe-error__reference"
          style={{
            fontSize: 'var(--text-caption-size)',
            lineHeight: 'var(--text-caption-lh)',
            color: 'var(--color-text-secondary)',
            fontVariantNumeric: 'tabular-nums',
            userSelect: 'all',
          }}
        >
          {t('ui.reference', { id: referenceId })}
        </span>
      )}
    </div>
  );
}
